import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiPost, getToken } from '@/utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Setup2FA() {
  const navigate = useNavigate();
  const location = useLocation();
  const [enabling, setEnabling] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState(null);

  const { user } = location.state || {};

  const handleEnable2FA = async () => {
    setEnabling(true);
    setError(null);
    try {
      // First enable 2FA for the user - use apiPost which includes auth token
      const enableResponse = await apiPost('/auth/2fa/enable', {});

      if (enableResponse.ok) {
        // Mark as prompted
        await apiPost('/settings/2fa-prompted', {});
        
        // Navigate to dashboard - 2FA is now enabled and current device is trusted
        navigate('/app/dashboard', { state: { user }, replace: true });
      } else {
        const errorData = await enableResponse.json().catch(() => ({}));
        const errorMsg = errorData.detail || 'Failed to enable 2FA. Please try again.';
        setError(errorMsg);
        console.error('2FA enable failed:', errorMsg);
      }
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setEnabling(false);
    }
  };

  const handleSkip = async () => {
    setSkipping(true);
    try {
      // Mark as prompted so we don't show this again - use apiPost with auth token
      await apiPost('/settings/2fa-prompted', {});
      
      // Proceed to dashboard
      navigate('/app/dashboard', { state: { user }, replace: true });
    } catch (error) {
      console.error('Error:', error);
      // Still navigate even if marking fails
      navigate('/app/dashboard', { state: { user }, replace: true });
    } finally {
      setSkipping(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-center">
          <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-5xl">🔐</span>
          </div>
          <h1 className="text-2xl font-bold text-white">
            Secure Your Account
          </h1>
          <p className="text-indigo-100 mt-2">
            Add an extra layer of protection
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              Enable Two-Factor Authentication?
            </h2>
            <p className="text-gray-600">
              Two-factor authentication adds an extra security layer by requiring a verification code sent to your email each time you log in from a new device.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-gray-50 rounded-xl p-4 mb-8">
            <h3 className="font-medium text-gray-900 mb-3">Benefits:</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Protects your financial data from unauthorized access</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Get notified when someone tries to access your account</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Trusted devices are remembered for 30 days</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Can be disabled anytime in Security settings</span>
              </li>
            </ul>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-2">
                <span className="text-red-500">⚠️</span>
                <div>
                  <p className="text-sm text-red-700 font-medium">Error</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleEnable2FA}
              disabled={enabling || skipping}
              data-testid="enable-2fa-setup-btn"
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all ${
                enabling || skipping
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
              }`}
            >
              {enabling ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                  Enabling...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <span>🛡️</span>
                  Yes, Protect My Account
                </span>
              )}
            </button>

            <button
              onClick={handleSkip}
              disabled={enabling || skipping}
              data-testid="skip-2fa-setup-btn"
              className={`w-full py-3 px-6 rounded-xl font-medium transition-all ${
                enabling || skipping
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {skipping ? 'Please wait...' : 'Maybe Later'}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-6">
            You can always enable or disable 2FA later in Settings → Security
          </p>
        </div>
      </div>
    </div>
  );
}

export default Setup2FA;
